import LandingContentTab from './LandingContentTab';
import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { Navigate } from 'react-router-dom';
import { Header } from '../../components/Header';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Avatar, AvatarFallback, AvatarImage } from '../../components/ui/avatar';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../../components/ui/dialog';
import { Input } from '../../components/ui/input';
import { 
  Shield, 
  IdentificationCard, 
  CurrencyEur, 
  CheckCircle, 
  XCircle, 
  Clock, 
  Users, 
  ChartBar,
  Eye,
  Phone,
  Star,
  Briefcase,
  Dog,
  MagnifyingGlass,
  Trash,
  ChatCircle,
  MapPin,
  EnvelopeSimple,
  Crown,
  Gift,
  Gear,
  Globe,
} from '@phosphor-icons/react';
import { motion } from 'framer-motion';
import { toast } from 'sonner';
import { getCityName, getServiceName, formatDate, REQUEST_STATUS } from '../../lib/constants';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

function StatCard({ title, value, icon: Icon, color }) {
  return (
    <Card data-testid={`stat-card-${title.toLowerCase().replace(/\s/g, '-')}`}>
      <CardContent className="p-4 sm:p-6">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-xs sm:text-sm text-muted-foreground">{title}</p>
            <p className="text-2xl sm:text-3xl font-bold mt-1">{value}</p>
          </div>
          <div className={`w-10 h-10 sm:w-12 sm:h-12 rounded-full ${color} flex items-center justify-center`}>
            <Icon weight="duotone" className="w-5 h-5 sm:w-6 sm:h-6 text-white" />
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

function VerificationCard({ provider, onApprove, onReject, processing }) {
  const status = provider.verification_status;
  const user = provider.user;

  return (
    <Card data-testid={`verification-card-${provider.provider_id}`}>
      <CardContent className="p-4 sm:p-6">
        <div className="flex items-start gap-4">
          <Avatar className="w-10 h-10 sm:w-12 sm:h-12">
            <AvatarImage src={user?.picture} />
            <AvatarFallback>{user?.name?.charAt(0) || '?'}</AvatarFallback>
          </Avatar>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-1 flex-wrap">
              <h3 className="font-semibold">{user?.name || 'N/A'}</h3>
              <Badge variant={status === 'pending' ? 'outline' : status === 'approved' ? 'default' : 'destructive'}>
                {status === 'pending' ? 'Pendente' : status === 'approved' ? 'Aprovado' : 'Rejeitado'}
              </Badge>
            </div>
            <p className="text-sm text-muted-foreground">{user?.email}</p>
            <p className="text-xs text-muted-foreground mt-1">
              Submetido em: {new Date(provider.verification_submitted_at || provider.created_at).toLocaleDateString('pt-PT')}
            </p>
          </div>
        </div>

        <div className="flex gap-3 mt-4">
          {provider.document_url && (
            <Dialog>
              <DialogTrigger asChild>
                <Button variant="outline" size="sm" data-testid="view-document-btn">
                  <Eye className="w-4 h-4 mr-1" /> Documento
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-2xl">
                <DialogHeader><DialogTitle>Documento de Identificação</DialogTitle></DialogHeader>
                <img src={`${API_URL.replace('/api', '')}${provider.document_url}`} alt="Documento" className="w-full rounded-lg" data-testid="document-preview" />
              </DialogContent>
            </Dialog>
          )}
          {provider.selfie_url && (
            <Dialog>
              <DialogTrigger asChild>
                <Button variant="outline" size="sm" data-testid="view-selfie-btn">
                  <Eye className="w-4 h-4 mr-1" /> Selfie
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-2xl">
                <DialogHeader><DialogTitle>Selfie</DialogTitle></DialogHeader>
                <img src={`${API_URL.replace('/api', '')}${provider.selfie_url}`} alt="Selfie" className="w-full rounded-lg" data-testid="selfie-preview" />
              </DialogContent>
            </Dialog>
          )}
        </div>

        {status === 'pending' && (
          <div className="flex gap-3 mt-4 pt-4 border-t">
            <Button onClick={() => onApprove(provider.provider_id)} disabled={processing} className="flex-1 rounded-full" data-testid="approve-verification-btn">
              <CheckCircle className="w-4 h-4 mr-2" /> Aprovar
            </Button>
            <Button variant="destructive" onClick={() => onReject(provider.provider_id)} disabled={processing} className="flex-1 rounded-full" data-testid="reject-verification-btn">
              <XCircle className="w-4 h-4 mr-2" /> Rejeitar
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function MbWayPaymentCard({ payment, onConfirm, onReject, processing }) {
  const user = payment.user;
  return (
    <Card data-testid={`mbway-payment-card-${payment.transaction_id}`}>
      <CardContent className="p-4 sm:p-6">
        <div className="flex items-start justify-between gap-4">
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 rounded-full bg-amber-100 flex items-center justify-center flex-shrink-0">
              <Phone weight="fill" className="w-5 h-5 text-amber-600" />
            </div>
            <div>
              <h3 className="font-semibold">{user?.name || 'N/A'}</h3>
              <p className="text-sm text-muted-foreground">{user?.email}</p>
              <p className="text-xs text-muted-foreground mt-1">
                Plano: <strong>{payment.plan_type === 'pro_monthly' ? 'Pro Mensal' : 'Pay-per-Contacto'}</strong>
              </p>
            </div>
          </div>
          <div className="text-right flex-shrink-0">
            <p className="text-xl font-bold">{payment.amount}&euro;</p>
            <Badge variant="outline"><Clock className="w-3 h-3 mr-1" />Pendente</Badge>
          </div>
        </div>
        <div className="flex gap-3 mt-4 pt-4 border-t">
          <Button onClick={() => onConfirm(payment.transaction_id)} disabled={processing} className="flex-1 rounded-full" data-testid="confirm-mbway-btn">
            <CheckCircle className="w-4 h-4 mr-2" /> Confirmar
          </Button>
          <Button variant="destructive" onClick={() => onReject(payment.transaction_id)} disabled={processing} className="flex-1 rounded-full" data-testid="reject-mbway-btn">
            <XCircle className="w-4 h-4 mr-2" /> Rejeitar
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

function UsersTab({ searchTerm }) {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const res = await fetch(`${API_URL}/admin/users`, { credentials: 'include' });
        if (res.ok) {
          const data = await res.json();
          setUsers(data.users || []);
        }
      } catch (error) {
        toast.error('Erro ao carregar utilizadores');
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const filtered = users.filter(u => {
    if (!searchTerm) return true;
    const term = searchTerm.toLowerCase();
    return (u.name || '').toLowerCase().includes(term) || (u.email || '').toLowerCase().includes(term) || (u.role || '').toLowerCase().includes(term);
  });

  if (loading) return <div className="space-y-3">{[1,2,3].map(i => <Card key={i} className="h-20 animate-pulse bg-muted" />)}</div>;

  return (
    <div className="space-y-3" data-testid="admin-users-list">
      <p className="text-sm text-muted-foreground mb-4">{filtered.length} utilizador{filtered.length !== 1 ? 'es' : ''}</p>
      {filtered.map(u => (
        <Card key={u.user_id} data-testid={`user-row-${u.user_id}`}>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <Avatar className="w-10 h-10">
                <AvatarImage src={u.picture} />
                <AvatarFallback>{u.name?.charAt(0) || '?'}</AvatarFallback>
              </Avatar>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <p className="font-semibold text-sm truncate">{u.name || 'Sem nome'}</p>
                  {u.role && (
                    <Badge variant={u.role === 'admin' ? 'default' : u.role === 'prestador' ? 'secondary' : 'outline'} className="text-xs">
                      {u.role === 'admin' ? 'Admin' : u.role === 'prestador' ? 'Prestador' : 'Cliente'}
                    </Badge>
                  )}
                  {!u.role && <Badge variant="outline" className="text-xs">Sem perfil</Badge>}
                </div>
                <div className="flex items-center gap-3 text-xs text-muted-foreground mt-0.5">
                  <span className="flex items-center gap-1"><EnvelopeSimple className="w-3 h-3" />{u.email}</span>
                  {u.city && <span className="flex items-center gap-1"><MapPin className="w-3 h-3" />{getCityName(u.city)}</span>}
                </div>
              </div>
              <div className="text-right text-xs text-muted-foreground flex-shrink-0">
                <p>{u.auth_method === 'google' ? 'Google' : 'Email'}</p>
                {u.created_at && <p>{formatDate(u.created_at)}</p>}
              </div>
            </div>
          </CardContent>
        </Card>
      ))}
      {filtered.length === 0 && (
        <Card><CardContent className="py-12 text-center">
          <Users weight="duotone" className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
          <p className="text-muted-foreground">Nenhum utilizador encontrado.</p>
        </CardContent></Card>
      )}
    </div>
  );
}

function ProvidersTab({ searchTerm }) {
  const [providers, setProviders] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const res = await fetch(`${API_URL}/admin/providers`, { credentials: 'include' });
        if (res.ok) {
          const data = await res.json();
          setProviders(data.providers || []);
        }
      } catch (error) {
        toast.error('Erro ao carregar prestadores');
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const filtered = providers.filter(p => {
    if (!searchTerm) return true;
    const term = searchTerm.toLowerCase();
    return (p.user?.name || '').toLowerCase().includes(term) || (p.user?.email || '').toLowerCase().includes(term) || (p.zones || []).some(z => z.includes(term));
  });

  if (loading) return <div className="space-y-3">{[1,2,3].map(i => <Card key={i} className="h-24 animate-pulse bg-muted" />)}</div>;

  return (
    <div className="space-y-3" data-testid="admin-providers-list">
      <p className="text-sm text-muted-foreground mb-4">{filtered.length} prestador{filtered.length !== 1 ? 'es' : ''}</p>
      {filtered.map(p => (
        <Card key={p.provider_id} data-testid={`provider-row-${p.provider_id}`}>
          <CardContent className="p-4">
            <div className="flex items-start gap-3">
              <Avatar className="w-10 h-10">
                <AvatarImage src={p.user?.picture} />
                <AvatarFallback>{p.user?.name?.charAt(0) || '?'}</AvatarFallback>
              </Avatar>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <p className="font-semibold text-sm">{p.user?.name || 'N/A'}</p>
                  {p.subscription_plan === 'pro' && <Badge className="bg-amber-500 text-white text-xs"><Crown className="w-3 h-3 mr-1" />Pro</Badge>}
                  {p.identity_verified && <Badge variant="default" className="text-xs"><CheckCircle className="w-3 h-3 mr-1" />Verificado</Badge>}
                  {p.is_launch_offer && <Badge variant="secondary" className="text-xs">Oferta Lanc.</Badge>}
                </div>
                <p className="text-xs text-muted-foreground">{p.user?.email}</p>
                <div className="flex items-center gap-3 mt-1 flex-wrap">
                  {(p.zones || []).length > 0 && (
                    <span className="text-xs text-muted-foreground flex items-center gap-1">
                      <MapPin className="w-3 h-3" />{p.zones.map(z => getCityName(z)).join(', ')}
                    </span>
                  )}
                  <span className="text-xs text-muted-foreground flex items-center gap-1">
                    <Star className="w-3 h-3" />{(p.rating || 0).toFixed(1)} ({p.review_count || 0})
                  </span>
                  {(p.services || []).length > 0 && (
                    <span className="text-xs text-muted-foreground">
                      {p.services.length} serviço{p.services.length !== 1 ? 's' : ''}
                    </span>
                  )}
                </div>
              </div>
              <div className="text-right flex-shrink-0">
                <Badge variant={p.verification_status === 'approved' ? 'default' : p.verification_status === 'pending' ? 'outline' : 'secondary'} className="text-xs">
                  {p.verification_status === 'approved' ? 'Aprovado' : p.verification_status === 'pending' ? 'Pendente' : p.verification_status || 'N/A'}
                </Badge>
              </div>
            </div>
          </CardContent>
        </Card>
      ))}
      {filtered.length === 0 && (
        <Card><CardContent className="py-12 text-center">
          <Briefcase weight="duotone" className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
          <p className="text-muted-foreground">Nenhum prestador encontrado.</p>
        </CardContent></Card>
      )}
    </div>
  );
}

function RequestsTab() {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const res = await fetch(`${API_URL}/admin/requests`, { credentials: 'include' });
        if (res.ok) {
          const data = await res.json();
          setRequests(data.requests || []);
        }
      } catch (error) {
        toast.error('Erro ao carregar pedidos');
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  if (loading) return <div className="space-y-3">{[1,2,3].map(i => <Card key={i} className="h-20 animate-pulse bg-muted" />)}</div>;

  return (
    <div className="space-y-3" data-testid="admin-requests-list">
      <p className="text-sm text-muted-foreground mb-4">{requests.length} pedido{requests.length !== 1 ? 's' : ''}</p>
      {requests.map(r => {
        const statusInfo = REQUEST_STATUS[r.status] || { label: r.status, color: 'bg-gray-100 text-gray-800' };
        return (
          <Card key={r.request_id} data-testid={`request-row-${r.request_id}`}>
            <CardContent className="p-4">
              <div className="flex items-center justify-between gap-3">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <p className="font-semibold text-sm">{getServiceName(r.service_type)}</p>
                    <span className={`text-xs px-2 py-0.5 rounded-full ${statusInfo.color}`}>{statusInfo.label}</span>
                  </div>
                  <div className="flex items-center gap-3 text-xs text-muted-foreground mt-1">
                    <span>Cliente: {r.client?.name || 'N/A'}</span>
                    {r.provider?.user && <span>Prestador: {r.provider.user.name}</span>}
                    {r.city && <span className="flex items-center gap-1"><MapPin className="w-3 h-3" />{getCityName(r.city)}</span>}
                  </div>
                </div>
                <div className="text-right text-xs text-muted-foreground flex-shrink-0">
                  {r.price && <p className="font-semibold text-sm text-foreground">{r.price}&euro;</p>}
                  <p>{r.start_date ? formatDate(r.start_date) : ''}</p>
                </div>
              </div>
            </CardContent>
          </Card>
        );
      })}
      {requests.length === 0 && (
        <Card><CardContent className="py-12 text-center">
          <Dog weight="duotone" className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
          <p className="text-muted-foreground">Nenhum pedido registado.</p>
        </CardContent></Card>
      )}
    </div>
  );
}

function ReviewsTab() {
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deleting, setDeleting] = useState(null);

  const fetchReviews = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/admin/reviews`, { credentials: 'include' });
      if (res.ok) {
        const data = await res.json();
        setReviews(data.reviews || []);
      }
    } catch (error) {
      toast.error('Erro ao carregar avaliações');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchReviews(); }, [fetchReviews]);

  const handleDelete = async (reviewId) => {
    setDeleting(reviewId);
    try {
      const res = await fetch(`${API_URL}/admin/reviews/${reviewId}`, { method: 'DELETE', credentials: 'include' });
      if (res.ok) {
        toast.success('Avaliação removida');
        fetchReviews();
      } else {
        const err = await res.json().catch(() => ({}));
        toast.error(err.detail || 'Erro ao remover');
      }
    } catch (error) {
      toast.error('Erro ao remover avaliação');
    } finally {
      setDeleting(null);
    }
  };

  if (loading) return <div className="space-y-3">{[1,2,3].map(i => <Card key={i} className="h-20 animate-pulse bg-muted" />)}</div>;

  return (
    <div className="space-y-3" data-testid="admin-reviews-list">
      <p className="text-sm text-muted-foreground mb-4">{reviews.length} avaliação{reviews.length !== 1 ? 'ões' : ''}</p>
      {reviews.map(r => (
        <Card key={r.review_id} data-testid={`review-row-${r.review_id}`}>
          <CardContent className="p-4">
            <div className="flex items-start justify-between gap-3">
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-1">
                  <div className="flex">
                    {Array.from({ length: 5 }).map((_, i) => (
                      <Star key={i} weight={i < r.rating ? 'fill' : 'regular'} className={`w-4 h-4 ${i < r.rating ? 'text-amber-500' : 'text-muted-foreground/30'}`} />
                    ))}
                  </div>
                  <span className="text-xs text-muted-foreground">{r.rating}/5</span>
                </div>
                {r.comment && <p className="text-sm text-muted-foreground mb-1">"{r.comment}"</p>}
                <div className="flex items-center gap-3 text-xs text-muted-foreground">
                  <span>De: {r.client?.name || 'N/A'}</span>
                  <span>Para: {r.provider?.user?.name || r.provider_id}</span>
                  {r.created_at && <span>{formatDate(r.created_at)}</span>}
                </div>
              </div>
              <Button variant="ghost" size="icon" onClick={() => handleDelete(r.review_id)} disabled={deleting === r.review_id} className="flex-shrink-0 text-destructive hover:text-destructive hover:bg-destructive/10" data-testid={`delete-review-${r.review_id}`}>
                <Trash className="w-4 h-4" />
              </Button>
            </div>
          </CardContent>
        </Card>
      ))}
      {reviews.length === 0 && (
        <Card><CardContent className="py-12 text-center">
          <Star weight="duotone" className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
          <p className="text-muted-foreground">Nenhuma avaliação registada.</p>
        </CardContent></Card>
      )}
    </div>
  );
}

function SettingsTab({ stats, onUpdate }) {
  const [maxFree, setMaxFree] = useState(stats?.launch_offer_max || 20);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (stats?.launch_offer_max !== undefined) setMaxFree(stats.launch_offer_max);
  }, [stats?.launch_offer_max]);

  const handleSave = async () => {
    setSaving(true);
    try {
      const res = await fetch(`${API_URL}/admin/launch-offer`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ max_free_providers: parseInt(maxFree) })
      });
      if (res.ok) {
        toast.success('Oferta de lançamento atualizada');
        onUpdate();
      } else {
        const err = await res.json().catch(() => ({}));
        toast.error(err.detail || 'Erro ao guardar');
      }
    } catch (error) {
      toast.error('Erro ao guardar definições');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="admin-settings">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base">
            <Gift weight="duotone" className="w-5 h-5 text-orange-500" />
            Oferta de Lançamento
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid sm:grid-cols-3 gap-4">
            <div className="p-4 rounded-lg bg-muted text-center">
              <p className="text-2xl font-bold">{stats?.launch_offer_max || 20}</p>
              <p className="text-xs text-muted-foreground mt-1">Máx. prestadores grátis</p>
            </div>
            <div className="p-4 rounded-lg bg-muted text-center">
              <p className="text-2xl font-bold text-green-600">{stats?.launch_offer_used || 0}</p>
              <p className="text-xs text-muted-foreground mt-1">Já inscritos grátis</p>
            </div>
            <div className="p-4 rounded-lg bg-muted text-center">
              <p className="text-2xl font-bold text-amber-600">{Math.max(0, (stats?.launch_offer_max || 20) - (stats?.launch_offer_used || 0))}</p>
              <p className="text-xs text-muted-foreground mt-1">Vagas restantes</p>
            </div>
          </div>
          <div className="flex items-end gap-3 pt-2">
            <div className="flex-1">
              <label className="text-sm font-medium mb-1.5 block">Número máximo de prestadores grátis</label>
              <Input
                type="number"
                min="0"
                value={maxFree}
                onChange={(e) => setMaxFree(e.target.value)}
                className="w-full"
                data-testid="launch-offer-max-input"
              />
            </div>
            <Button onClick={handleSave} disabled={saving} className="rounded-full" data-testid="save-launch-offer-btn">
              {saving ? 'A guardar...' : 'Guardar'}
            </Button>
          </div>
          <p className="text-xs text-muted-foreground">
            Quando o limite for atingido, novos prestadores terão de escolher um plano pago para se registar.
          </p>
        </CardContent>
      </Card>
    </div>
  );
}

export default function AdminDashboard() {
  const { user, loading: authLoading } = useAuth();
  const [stats, setStats] = useState(null);
  const [verifications, setVerifications] = useState([]);
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(false);
  const [activeTab, setActiveTab] = useState('overview');
  const [searchTerm, setSearchTerm] = useState('');

  const fetchAll = useCallback(async () => {
    try {
      const [statsRes, verifsRes, paymentsRes] = await Promise.all([
        fetch(`${API_URL}/admin/stats`, { credentials: 'include' }),
        fetch(`${API_URL}/admin/verifications`, { credentials: 'include' }),
        fetch(`${API_URL}/admin/mbway-payments`, { credentials: 'include' })
      ]);

      if (statsRes.ok) setStats(await statsRes.json());
      if (verifsRes.ok) {
        const d = await verifsRes.json();
        setVerifications(d.verifications || []);
      }
      if (paymentsRes.ok) {
        const d = await paymentsRes.json();
        setPayments(d.payments || []);
      }
    } catch (error) {
      toast.error('Erro ao carregar dados do admin');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (user?.role === 'admin') fetchAll();
  }, [user, fetchAll]);

  const handleVerification = async (providerId, approved) => {
    setProcessing(true);
    try {
      const res = await fetch(`${API_URL}/admin/verify-provider/${providerId}?approved=${approved}`, { method: 'PUT', credentials: 'include' });
      if (res.ok) {
        toast.success(approved ? 'Verificação aprovada' : 'Verificação rejeitada');
        fetchAll();
      } else {
        const err = await res.json();
        toast.error(err.detail || 'Erro');
      }
    } catch (error) {
      toast.error('Erro ao processar verificação');
    } finally {
      setProcessing(false);
    }
  };

  const handleMbWay = async (transactionId, action) => {
    setProcessing(true);
    try {
      const res = await fetch(`${API_URL}/admin/mbway-payments/${transactionId}/${action}`, { method: 'PUT', credentials: 'include' });
      if (res.ok) {
        toast.success(action === 'confirm' ? 'Pagamento confirmado' : 'Pagamento rejeitado');
        fetchAll();
      } else {
        const err = await res.json();
        toast.error(err.detail || 'Erro');
      }
    } catch (error) {
      toast.error('Erro ao processar pagamento');
    } finally {
      setProcessing(false);
    }
  };

  if (authLoading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!user || user.role !== 'admin') {
    return <Navigate to="/" replace />;
  }

  const pendingVerifications = verifications.filter(v => v.verification_status === 'pending');
  const processedVerifications = verifications.filter(v => v.verification_status !== 'pending');

  return (
    <div className="min-h-screen bg-background" data-testid="admin-dashboard">
      <Header />
      
      <main className="container mx-auto px-4 py-6 sm:py-8">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
          <div className="flex items-center gap-3 mb-6 sm:mb-8">
            <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-full bg-primary flex items-center justify-center">
              <Shield weight="fill" className="w-5 h-5 sm:w-6 sm:h-6 text-primary-foreground" />
            </div>
            <div>
              <h1 className="text-2xl sm:text-3xl font-bold font-outfit">Painel de Administração</h1>
              <p className="text-sm text-muted-foreground">Gerir utilizadores, prestadores, verificações e pagamentos</p>
            </div>
          </div>

          {/* Stats */}
          {stats && (
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-7 gap-3 sm:gap-4 mb-6 sm:mb-8">
              <StatCard title="Utilizadores" value={stats.total_users} icon={Users} color="bg-blue-500" />
              <StatCard title="Prestadores" value={stats.total_providers} icon={Briefcase} color="bg-green-500" />
              <StatCard title="Verificações" value={stats.pending_verifications} icon={Shield} color="bg-amber-500" />
              <StatCard title="MB Way" value={stats.pending_mbway_payments} icon={CurrencyEur} color="bg-purple-500" />
              <StatCard title="Pedidos" value={stats.total_requests} icon={ChartBar} color="bg-red-500" />
              <StatCard title="Avaliações" value={stats.total_reviews} icon={Star} color="bg-amber-600" />
              <StatCard title="Mensagens" value={stats.total_messages} icon={ChatCircle} color="bg-sky-500" />
            </div>
          )}

          {/* Search bar for users/providers tabs */}
          {(activeTab === 'users' || activeTab === 'providers') && (
            <div className="relative mb-4">
              <MagnifyingGlass className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                placeholder={activeTab === 'users' ? 'Pesquisar utilizadores por nome, email ou role...' : 'Pesquisar prestadores por nome, email ou cidade...'}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-9"
                data-testid="admin-search-input"
              />
            </div>
          )}

          <Tabs value={activeTab} onValueChange={(v) => { setActiveTab(v); setSearchTerm(''); }}>
            <TabsList className="mb-6 flex-wrap h-auto gap-1">
              <TabsTrigger value="overview" data-testid="admin-tab-overview">Resumo</TabsTrigger>
              <TabsTrigger value="users" data-testid="admin-tab-users">
                Utilizadores
                {stats && <Badge variant="secondary" className="ml-1.5 text-xs">{stats.total_users}</Badge>}
              </TabsTrigger>
              <TabsTrigger value="providers" data-testid="admin-tab-providers">
                Prestadores
                {stats && <Badge variant="secondary" className="ml-1.5 text-xs">{stats.total_providers}</Badge>}
              </TabsTrigger>
              <TabsTrigger value="verifications" data-testid="admin-tab-verifications">
                Verificações
                {pendingVerifications.length > 0 && <Badge variant="destructive" className="ml-1.5 h-5 w-5 p-0 justify-center rounded-full text-xs">{pendingVerifications.length}</Badge>}
              </TabsTrigger>
              <TabsTrigger value="mbway" data-testid="admin-tab-mbway">
                MB Way
                {payments.length > 0 && <Badge variant="destructive" className="ml-1.5 h-5 w-5 p-0 justify-center rounded-full text-xs">{payments.length}</Badge>}
              </TabsTrigger>
              <TabsTrigger value="requests" data-testid="admin-tab-requests">Pedidos</TabsTrigger>
              <TabsTrigger value="reviews" data-testid="admin-tab-reviews">Avaliações</TabsTrigger>
              <TabsTrigger value="settings" data-testid="admin-tab-settings">
                <Gear className="w-4 h-4 mr-1" />Definições
              </TabsTrigger>
                  <TabsTrigger value="landing" data-testid="admin-tab-landing">
  <Globe className="w-4 h-4 mr-1" />Conteúdo do Site
</TabsTrigger>
            </TabsList>

            {/* Overview Tab */}
            <TabsContent value="overview">
              <div className="grid md:grid-cols-2 gap-6">
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2 text-base">
                      <Shield weight="duotone" className="w-5 h-5 text-amber-500" />
                      Verificações Pendentes
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    {pendingVerifications.length > 0 ? (
                      <div className="space-y-3">
                        {pendingVerifications.slice(0, 3).map(v => (
                          <div key={v.provider_id} className="flex items-center gap-3">
                            <Avatar className="w-8 h-8"><AvatarFallback>{v.user?.name?.charAt(0) || '?'}</AvatarFallback></Avatar>
                            <div className="flex-1 min-w-0">
                              <p className="text-sm font-medium truncate">{v.user?.name}</p>
                              <p className="text-xs text-muted-foreground">{v.user?.email}</p>
                            </div>
                            <Badge variant="outline" className="text-xs">Pendente</Badge>
                          </div>
                        ))}
                        {pendingVerifications.length > 3 && <p className="text-xs text-muted-foreground">+{pendingVerifications.length - 3} mais</p>}
                        <Button variant="outline" size="sm" onClick={() => setActiveTab('verifications')} className="w-full mt-2">Ver Todas</Button>
                      </div>
                    ) : (
                      <p className="text-sm text-muted-foreground">Nenhuma verificação pendente.</p>
                    )}
                  </CardContent>
                </Card>
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2 text-base">
                      <CurrencyEur weight="duotone" className="w-5 h-5 text-purple-500" />
                      Pagamentos MB Way Pendentes
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    {payments.length > 0 ? (
                      <div className="space-y-3">
                        {payments.slice(0, 3).map(p => (
                          <div key={p.transaction_id} className="flex items-center justify-between">
                            <div className="flex-1 min-w-0">
                              <p className="text-sm font-medium truncate">{p.user?.name || 'N/A'}</p>
                              <p className="text-xs text-muted-foreground">{p.plan_type === 'pro_monthly' ? 'Pro Mensal' : 'Pay-per-Contacto'}</p>
                            </div>
                            <p className="font-semibold text-sm">{p.amount}&euro;</p>
                          </div>
                        ))}
                        {payments.length > 3 && <p className="text-xs text-muted-foreground">+{payments.length - 3} mais</p>}
                        <Button variant="outline" size="sm" onClick={() => setActiveTab('mbway')} className="w-full mt-2">Ver Todos</Button>
                      </div>
                    ) : (
                      <p className="text-sm text-muted-foreground">Nenhum pagamento pendente.</p>
                    )}
                  </CardContent>
                </Card>

                {/* Launch Offer Card */}
                {stats && (
                  <Card className="md:col-span-2">
                    <CardHeader>
                      <CardTitle className="flex items-center gap-2 text-base">
                        <Gift weight="duotone" className="w-5 h-5 text-orange-500" />
                        Oferta de Lançamento
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="flex items-center gap-6">
                        <div className="flex-1">
                          <div className="flex justify-between text-sm mb-2">
                            <span className="text-muted-foreground">Progresso</span>
                            <span className="font-medium">{stats.launch_offer_used}/{stats.launch_offer_max} vagas ocupadas</span>
                          </div>
                          <div className="w-full bg-muted rounded-full h-3">
                            <div
                              className="bg-gradient-to-r from-amber-500 to-orange-500 h-3 rounded-full transition-all"
                              style={{ width: `${Math.min(100, (stats.launch_offer_used / stats.launch_offer_max) * 100)}%` }}
                            />
                          </div>
                          <p className="text-xs text-muted-foreground mt-2">
                            {Math.max(0, stats.launch_offer_max - stats.launch_offer_used)} vagas restantes
                          </p>
                        </div>
                        <Button variant="outline" size="sm" onClick={() => setActiveTab('settings')} className="rounded-full">
                          <Gear className="w-4 h-4 mr-1" /> Gerir
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                )}
              </div>
            </TabsContent>

            {/* Users Tab */}
            <TabsContent value="users"><UsersTab searchTerm={searchTerm} /></TabsContent>

            {/* Providers Tab */}
            <TabsContent value="providers"><ProvidersTab searchTerm={searchTerm} /></TabsContent>

            {/* Verifications Tab */}
            <TabsContent value="verifications">
              {loading ? (
                <div className="space-y-4">{[1, 2].map(i => <Card key={i} className="h-40 animate-pulse bg-muted" />)}</div>
              ) : (
                <>
                  {pendingVerifications.length > 0 && (
                    <div className="mb-6">
                      <h2 className="text-lg font-semibold mb-3">Pendentes ({pendingVerifications.length})</h2>
                      <div className="space-y-4">
                        {pendingVerifications.map(v => (
                          <VerificationCard key={v.provider_id} provider={v} onApprove={(id) => handleVerification(id, true)} onReject={(id) => handleVerification(id, false)} processing={processing} />
                        ))}
                      </div>
                    </div>
                  )}
                  {processedVerifications.length > 0 && (
                    <div>
                      <h2 className="text-lg font-semibold mb-3">Histórico ({processedVerifications.length})</h2>
                      <div className="space-y-4">
                        {processedVerifications.map(v => (
                          <VerificationCard key={v.provider_id} provider={v} onApprove={(id) => handleVerification(id, true)} onReject={(id) => handleVerification(id, false)} processing={processing} />
                        ))}
                      </div>
                    </div>
                  )}
                  {verifications.length === 0 && (
                    <Card><CardContent className="py-12 text-center">
                      <IdentificationCard weight="duotone" className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                      <h3 className="font-semibold mb-2">Sem verificações</h3>
                      <p className="text-muted-foreground text-sm">Nenhuma verificação de identidade submetida.</p>
                    </CardContent></Card>
                  )}
                </>
              )}

<TabsContent value="landing">
  <LandingContentTab />
</TabsContent>

            </TabsContent>

            {/* MB Way Tab */}
            <TabsContent value="mbway">
              {loading ? (
                <div className="space-y-4">{[1, 2].map(i => <Card key={i} className="h-40 animate-pulse bg-muted" />)}</div>
              ) : payments.length > 0 ? (
                <div className="space-y-4">
                  {payments.map(p => (
                    <MbWayPaymentCard key={p.transaction_id} payment={p} onConfirm={(id) => handleMbWay(id, 'confirm')} onReject={(id) => handleMbWay(id, 'reject')} processing={processing} />
                  ))}
                </div>
              ) : (
                <Card><CardContent className="py-12 text-center">
                  <CurrencyEur weight="duotone" className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                  <h3 className="font-semibold mb-2">Sem pagamentos pendentes</h3>
                  <p className="text-muted-foreground text-sm">Nenhum pagamento MB Way a aguardar confirmação.</p>
                </CardContent></Card>
              )}
            </TabsContent>

            {/* Requests Tab */}
            <TabsContent value="requests"><RequestsTab /></TabsContent>

            {/* Reviews Tab */}
            <TabsContent value="reviews"><ReviewsTab /></TabsContent>

            {/* Settings Tab */}
            <TabsContent value="settings"><SettingsTab stats={stats} onUpdate={fetchAll} /></TabsContent>
          </Tabs>
        </motion.div>
      </main>
    </div>
  );
}
